import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

export type Toast = {
  readonly id: number;
  readonly message: string;
  readonly tone: string;
  readonly undo?: () => void;
};

export const TOAST_TTL_MS = 4200;

// A toast that offers Undo stays long enough to be read and acted on. §10
export const TOAST_UNDO_TTL_MS = 8000;

export type Toaster = {
  readonly toasts: readonly Toast[];
  readonly push: (message: string, tone: string, undo?: () => void) => void;
};

// The toast plus the tile's pulse are the confirmation an action worked. §7
export function useToasts(): Toaster {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const next = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const push = useCallback((message: string, tone: string, undo?: () => void) => {
    next.current += 1;
    const id = next.current;
    setToasts((current) => [...current, { id, message, tone, undo }]);
    timers.current.push(
      setTimeout(
        () => setToasts((current) => current.filter((t) => t.id !== id)),
        undo === undefined ? TOAST_TTL_MS : TOAST_UNDO_TTL_MS,
      ),
    );
  }, []);

  return { toasts, push };
}

export function Toasts({
  toasts,
  undoLabel,
}: {
  toasts: readonly Toast[];
  undoLabel: string;
}): ReactElement {
  return (
    <div className="pointer-events-none fixed end-4 bottom-4 z-90 mb-safe flex flex-col gap-2">
      {toasts.map((toast) => (
        <p
          key={toast.id}
          role="status"
          className="pointer-events-auto flex max-w-[min(360px,90vw)] items-start gap-2 rounded-xl border bg-card px-4 py-2.5 text-sm font-medium shadow-lg"
        >
          <i aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${toast.tone}`} />
          {toast.message}
          {toast.undo !== undefined && (
            <button
              type="button"
              onClick={toast.undo}
              className="ms-auto shrink-0 font-bold text-primary underline"
            >
              {undoLabel}
            </button>
          )}
        </p>
      ))}
    </div>
  );
}
